import { useEffect, useState } from 'react';
import type { TrackState } from '../api.js';
import StateBadge from './StateBadge.js';

interface TrackEvent {
  from_state: TrackState | null;
  to_state: TrackState;
  reason: string | null;
  created_at: string;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function absoluteTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString();
}

export default function EventTimeline({ trackId }: { trackId: string }) {
  const [events, setEvents] = useState<TrackEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`/api/tracks/${trackId}/events`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `request failed: ${res.status}`);
        }
        const body = (await res.json()) as { events: TrackEvent[] };
        if (!cancelled) {
          setEvents(body.events);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [trackId]);

  if (error) {
    return (
      <div className="section">
        <h3>Timeline</h3>
        <div className="errors">{error}</div>
      </div>
    );
  }

  if (events === null) {
    return (
      <div className="section">
        <h3>Timeline</h3>
        <div className="empty">Loading…</div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="section">
        <h3>Timeline</h3>
        <div className="empty">No events yet.</div>
      </div>
    );
  }

  return (
    <div className="section">
      <h3>Timeline</h3>
      <ol
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          borderLeft: '2px solid var(--border)',
        }}
      >
        {events.map((ev, i) => (
          <li
            key={i}
            style={{
              position: 'relative',
              paddingLeft: 16,
              paddingBottom: 14,
              marginLeft: 4,
            }}
          >
            <span
              aria-hidden
              style={{
                position: 'absolute',
                left: -7,
                top: 6,
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'var(--accent)',
              }}
            />
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: 2,
              }}
            >
              <StateBadge state={ev.to_state} />
              {ev.from_state && (
                <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                  from {ev.from_state}
                </span>
              )}
            </div>
            {ev.reason && (
              <div style={{ fontSize: 13, marginBottom: 4 }}>{ev.reason}</div>
            )}
            <div style={{ color: 'var(--muted)', fontSize: 12 }}>
              {relativeTime(ev.created_at)} · {absoluteTime(ev.created_at)}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
